import React from "react";
import {
  BarChart3,
  Plus,
  Image,
  BarChartHorizontal,
  Users,
  LogOut,
  CreditCard,
} from "lucide-react";
import { SidebarProps } from "../../types";
import { useAuth } from "../../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { isUsersOnlyAdmin } from "../../config/roles";
import { hasSection, type AdminSection } from "../../config/products";
import { useProduct } from "../../context/ProductContext";

const Sidebar: React.FC<SidebarProps> = ({ activeTab, onTabChange }) => {
  const { logout, userEmail } = useAuth();
  const { product } = useProduct();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const allMenuItems: { id: number; label: string; icon: typeof BarChart3; section: AdminSection }[] = [
    { id: 0, label: "Dashboard", icon: BarChart3, section: "dashboard" },
    { id: 1, label: "Add Template", icon: Plus, section: "templates" },
    { id: 2, label: "Categories", icon: BarChartHorizontal, section: "templates" },
    { id: 3, label: "Users", icon: Users, section: "users" },
    { id: 4, label: "Billing", icon: CreditCard, section: "billing" },
  ];

  // Restricted admins only see the Users screen.
  const menuItems = isUsersOnlyAdmin(userEmail)
    ? allMenuItems.filter((item) => item.id === 3)
    : allMenuItems.filter((item) => hasSection(product, item.section));

  return (
    <div className="w-72 h-full flex flex-col bg-[#181a29] border-r border-white/5">
      {/* Brand */}
      <div className="flex items-center gap-3 px-5 py-5 border-b border-white/5">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center shadow-lg shadow-violet-600/20">
          <Image className="w-5 h-5 text-white" />
        </div>
        <div>
          <h1 className="text-white font-semibold text-base leading-tight">
            AI Poster
          </h1>
          <p className="text-xs text-gray-400">Admin Dashboard</p>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        {menuItems.map((item) => {
          const IconComponent = item.icon;
          const isActive = activeTab === item.id;
          return (
            <button
              key={item.id}
              onClick={() => onTabChange(item.id)}
              className={`w-full flex items-center gap-3 px-3.5 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                isActive
                  ? "bg-gradient-to-r from-violet-600 to-indigo-600 text-white shadow-lg shadow-violet-600/20"
                  : "text-gray-400 hover:bg-white/[0.06] hover:text-white"
              }`}
            >
              <IconComponent className="w-[18px] h-[18px] shrink-0" />
              <span className="text-left">{item.label}</span>
            </button>
          );
        })}
      </nav>

      {/* Footer: user + branding */}
      <div className="mt-auto p-3 border-t border-white/5 space-y-3">
        <div className="flex items-center gap-3 px-2">
          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center text-white text-sm font-semibold shrink-0">
            {userEmail?.charAt(0).toUpperCase() || "A"}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-white truncate">Admin</p>
            <p className="text-xs text-gray-500 truncate">{userEmail}</p>
          </div>
          <button
            onClick={handleLogout}
            title="Logout"
            className="w-8 h-8 inline-flex items-center justify-center rounded-lg text-gray-400 hover:bg-red-500/10 hover:text-red-400 transition-colors shrink-0"
          >
            <LogOut className="w-4 h-4" />
          </button>
        </div>

        <div className="flex items-center gap-2.5 px-3 py-2.5 rounded-xl bg-white/[0.04] border border-white/5">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center shrink-0">
            <Image className="w-4 h-4 text-white" />
          </div>
          <div className="min-w-0">
            <p className="text-xs font-semibold text-white">
              AI Poster <span className="text-gray-500 font-normal">v1.0.0</span>
            </p>
            <p className="text-[11px] text-gray-500">© 2026 AI Poster</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
